import AppleStoreIcon from '@/components/icons/AppleStoreIcon/AppleStoreIcon';
import FacebookIcon from '@/components/icons/FacebookIcon/FacebookIcon';
import GooglePlayIcon from '@/components/icons/GooglePlayIcon/GooglePlayIcon';
import LinkedInIcon from '@/components/icons/LinkedInIcon/LinkedInIcon';
import RVULogo from '@/components/icons/RVULogo/RVULogo';
import { RVULogoNew } from '@/components/icons/RVULogo/RVULogoNew';
import { TempcoverLogo } from '@/components/icons/TempcoverLogo/TempcoverLogo';
import XLogo from '@/components/icons/XLogo/XLogo';
import YoutubeIcon from '@/components/icons/YoutubeIcon/YoutubeIcon';
import { FooterData } from '@/types/footer';

function Footer({ data }: { data: FooterData }) {
  if (!data) return;

  return (
    <footer>
      <div className="tc-container py-16">
        <div className="grid grid-cols-1 items-start gap-4 lg:grid-cols-[1fr_1fr_1fr]">
          {/* Copyright and address */}
          <div className="order-1 flex flex-col items-center justify-between md:items-start">
            <TempcoverLogo className="mb-4 lg:mb-6" />

            {/* Address and Copyright - Desktop */}
            <div className="hidden flex-col gap-2 lg:flex">
              <div dangerouslySetInnerHTML={{ __html: data.info }} />

              <div className="block w-[40vw] max-w-[150px] lg:hidden">
                <RVULogo />
              </div>
            </div>
          </div>

          {/* Links */}
          <div className="order-4 flex justify-between md:order-2">
            <nav>
              <h2 className="font-base text-[18px] lg:text-[23px]">{data.section1Title}</h2>
              <ul>
                {data.section1Items.map((item, idx) => (
                  <li key={idx}>
                    <a href={item.url} target={item.isExternal ? '_blank' : '_self'}>
                      {item.title}
                    </a>
                  </li>
                ))}
              </ul>
            </nav>

            <nav className="text-right lg:text-left">
              <h2 className="font-base text-[18px] lg:text-[23px]">{data.section2Title}</h2>
              <ul>
                {data.section2Items.map((item, idx) => (
                  <li key={idx}>
                    <a href={item.url} target={item.isExternal ? '_blank' : '_self'}>
                      {item.title}
                    </a>
                  </li>
                ))}
              </ul>
            </nav>
          </div>

          {/* Social Media Links & Signup */}
          <div className="order-2 lg:order-3">
            <h2 className="font-base text-[18px] lg:text-[23px]">{data.section3Title}</h2>
            <div dangerouslySetInnerHTML={{ __html: data.section3Content }} />

            <ul className="mt-4 flex flex-row gap-[10px] lg:mt-6">
              <li>
                <a title="X" href="https://x.com/tempcover" target="_blank" rel="noreferrer">
                  <div className="bg-primary fill-neutral active:bg-primary-400 flex h-[32px] w-[32px] content-center justify-center rounded pt-[3px]">
                    <XLogo />
                  </div>
                </a>
              </li>
              <li>
                <a title="Facebook" href="https://www.facebook.com/tempcover" target="_blank" rel="noreferrer">
                  <div className="bg-primary active:bg-primary-400 fill-neutral flex h-[32px] w-[32px] content-center justify-center rounded pt-[3px] pt-[6px]">
                    <FacebookIcon />
                  </div>
                </a>
              </li>
              <li>
                <a title="YouTube" href="https://www.youtube.com/user/tempcover" target="_blank" rel="noreferrer">
                  <div className="bg-primary active:bg-primary-400 fill-neutral flex h-[32px] w-[32px] content-center justify-center rounded pt-[3px] pt-[7px]">
                    <YoutubeIcon />
                  </div>
                </a>
              </li>
              <li>
                <a
                  title="LinkedIn"
                  href="https://www.linkedin.com/company/temporary-cover-ltd/?trk=tyah"
                  target="_blank"
                  rel="noreferrer"
                >
                  <div className="bg-primary active:bg-primary-400 fill-neutral flex h-[32px] w-[32px] content-center justify-center rounded pt-[5px] pt-[8px]">
                    <LinkedInIcon />
                  </div>
                </a>
              </li>
            </ul>
          </div>

          {/* App Download Links */}
          <div className="order-3 py-2 md:order-4 md:col-start-1">
            <h2 className="font-base text-[18px] md:hidden lg:font-[23px]">Get the app</h2>
            <div className="flex gap-[14px]">
              <AppStoreLinks appStore={data.storeLinks.appStoreLink} playStore={data.storeLinks.playStoreLink} />
            </div>
          </div>
        </div>

        <div
          className="mt-4 mb-4 w-full text-center md:hidden md:text-left [&_p]:text-[12px]"
          dangerouslySetInnerHTML={{ __html: data.secondaryContent ?? '' }}
        />
        <div
          className="mt-4 hidden w-full text-center md:block md:text-left [&_p]:text-[14px]"
          dangerouslySetInnerHTML={{ __html: data.secondaryContent ?? '' }}
        />

        {/* Address and Copyright - Mobile */}
        <div className="order-5 block text-center md:hidden [&_p]:text-[12px]">
          <div dangerouslySetInnerHTML={{ __html: data.info }} />
        </div>

        {/* Logo and Copyright */}
        <section className="border-dark-200 border-t bg-gray-50 md:mt-8">
          <div className="mx-auto flex max-w-7xl flex-col-reverse items-center justify-between md:flex-row md:py-6">
            <a
              href="https://www.rvu.co.uk/"
              target="_blank"
              rel="noreferrer"
              aria-label="Open RVU website in a new tab"
            >
              <RVULogoNew width={75} className="w-[50px] md:w-[75px]" />
            </a>
            <p className="mt-4 text-[14px] text-gray-700 md:text-[16px]">{data.footNote}</p>
          </div>
        </section>
      </div>
    </footer>
  );
}

export const AppStoreLinks = ({ appStore, playStore }: { appStore: string; playStore: string }) => (
  <>
    <a aria-label="iOS App Store" href={appStore} target="_blank" className="h-[48px]">
      <AppleStoreIcon />
    </a>
    <a aria-label="Google Play Store" href={playStore} target="_blank" className="h-[48px] w-[120px]">
      <GooglePlayIcon className="w-[135px]" />
    </a>
  </>
);

export default Footer;
